'use client';

import { useState, FormEvent } from 'react';
import { Button } from '../ui/button';
import { Input, Textarea, Select, Checkbox } from '../ui/form';
import { OrderAttributeDto, CreateOrderAttributeRequest } from '../../types';
import { useCreateOrderAttribute, useUpdateOrderAttribute } from '../../hooks/use-order-attributes';
import { AlertCircle, Check, Type, Hash, ToggleLeft, Calendar, ShieldCheck } from 'lucide-react';

interface OrderAttributeFormProps {
  workflowId: string;
  field?: OrderAttributeDto | null;
  onSuccess: () => void;
  onCancel: () => void;
  isInherited?: boolean;
}

export function OrderAttributeForm({
  workflowId,
  field,
  onSuccess,
  onCancel,
  isInherited = false,
}: OrderAttributeFormProps) {
  const isEditing = !!field;
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    jsonPath: field?.jsonPath || '$.', 
    label: field?.label || '',
    fieldType: field?.fieldType || 'string',
    description: field?.description || '',
    minStep: field?.minStep?.toString() || '',
    maxStep: field?.maxStep?.toString() || '',
    editableByRoleIds: field?.editableByRoleIds || [],
    displayOrder: field?.displayOrder?.toString() || '0',
    isRequired: field?.isRequired ?? false,
    isEditable: field?.isEditable ?? true,
    isActive: field?.isActive ?? true,
    validationRules: field?.validationRules || '',
    showValueInList: field?.showValueInList ?? false,
    allowedValuesJsonPath: field?.allowedValuesJsonPath || '',
  });

  const createField = useCreateOrderAttribute(workflowId);
  const updateField = useUpdateOrderAttribute(workflowId, field?.id || '');

  const handleChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      setValidationError(null);

      // Validate JSON path
      if (!formData.jsonPath.startsWith('$.')) {
        setValidationError('JSON path must start with $.');
        return;
      }

      // Validate label
      if (!formData.label.trim()) {
        setValidationError('Label is required');
        return;
      }

      // Parse steps
      const minLevel = formData.minStep ? parseInt(formData.minStep, 10) : undefined;
      const maxLevel = formData.maxStep ? parseInt(formData.maxStep, 10) : undefined;

      // Validate steps
      if (minLevel && maxLevel && minLevel > maxLevel) {
        setValidationError('Minimum step cannot be greater than maximum');
        return;
      }

      // Parse validation rules if provided
      if (formData.validationRules) {
        try {
          JSON.parse(formData.validationRules);
        } catch {
          setValidationError('Validation rules must be valid JSON');
          return;
        }
      }

      if (isEditing && field?.id) {
        await updateField.mutateAsync({
          jsonPath: formData.jsonPath,
          fieldType: formData.fieldType as 'string' | 'number' | 'boolean' | 'date',
          label: formData.label,
          description: formData.description || undefined,
          isActive: formData.isActive,
          minStep: minLevel,
          maxStep: maxLevel,
          editableByRoleIds: formData.editableByRoleIds.length > 0 ? formData.editableByRoleIds : undefined,
          displayOrder: parseInt(formData.displayOrder, 10),
          isRequired: formData.isRequired,
          isEditable: formData.isEditable,
          showValueInList: formData.showValueInList,
          allowedValuesJsonPath: formData.allowedValuesJsonPath || undefined,
        });
      } else {
        const payload: CreateOrderAttributeRequest = {
          workflowId,
          jsonPath: formData.jsonPath,
          label: formData.label,
          fieldType: formData.fieldType as 'string' | 'number' | 'boolean' | 'date',
          description: formData.description || undefined,
          minStep: minLevel,
          maxStep: maxLevel,
          editableByRoleIds: formData.editableByRoleIds.length > 0 ? formData.editableByRoleIds : undefined,
          displayOrder: parseInt(formData.displayOrder, 10),
          isRequired: formData.isRequired,
          isEditable: formData.isEditable,
          showValueInList: formData.showValueInList,
          allowedValuesJsonPath: formData.allowedValuesJsonPath || undefined,
          validationRules: formData.validationRules || undefined,
        };
        await createField.mutateAsync(payload);
      }

      onSuccess();
    } catch (error: any) {
      setValidationError(error.message || 'An error occurred while saving the field');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldTypeIcons = {
    string: <Type className="h-4 w-4 inline-block" />,
    number: <Hash className="h-4 w-4 inline-block" />,
    boolean: <ToggleLeft className="h-4 w-4 inline-block" />,
    date: <Calendar className="h-4 w-4 inline-block" />,
  };

  const fieldTypeDescriptions = {
    string: 'Text input for strings, notes, or short descriptions',
    number: 'Numeric input for amounts, quantities, or counts',
    boolean: 'Toggle switch for yes/no or true/false values',
    date: 'Date picker for selecting dates',
  };

  const examplePaths = {
    string: '$.Notes',
    number: '$.Order_Entries[0].QuantityOnOrder',
    boolean: '$.IsRushOrder',
    date: '$.DeliveryDate',
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Inherited attribute callout */}
      {isInherited && (
        <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-amber-800">Division Required Attribute</h4>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
              This attribute is required by the division. Label, JSON path, and type are managed at the
              division level. You can still customize the step range and role restrictions for this workflow.
            </p>
          </div>
        </div>
      )}

      {/* Validation Error Alert */}
      {validationError && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-red-800">Validation Error</h4>
            <p className="text-sm text-red-700 dark:text-red-400 mt-1">{validationError}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* Label */}
        <div className="col-span-2">
          <Input
            label="Field Label *"
            value={formData.label}
            onChange={(e) => handleChange('label', e.target.value)}
            placeholder="e.g., Order Notes, Delivery Date"
            disabled={isInherited}
          />
        </div>

        {/* JSON Path */}
        <div className="col-span-2">
          <Input
            label="JSON Path *"
            value={formData.jsonPath}
            onChange={(e) => handleChange('jsonPath', e.target.value)}
            placeholder="$.FieldName"
            className="font-mono text-sm"
            helperText={`Example for ${formData.fieldType}: ${examplePaths[formData.fieldType as keyof typeof examplePaths]}`}
            disabled={isInherited}
          />
        </div>

        {/* Allowed Values JSON Path */}
        <div className="col-span-2">
          <Input
            label="Allowed Values JSON Path"
            value={formData.allowedValuesJsonPath}
            onChange={(e) => handleChange('allowedValuesJsonPath', e.target.value)}
            placeholder="$.Order_Attributes[?(@.Name == 'FieldName')].AllowedValues"
            className="font-mono text-sm"
            helperText="Optional JSONPath to an array of {Name, Value} objects in the order document. When set, the UI renders a select list."
          />
        </div>

        {/* Field Type */}
        <div>
          <Select
            label="Field Type *"
            value={formData.fieldType}
            onChange={(e) => handleChange('fieldType', e.target.value)}
            helperText={fieldTypeDescriptions[formData.fieldType as keyof typeof fieldTypeDescriptions]}
            disabled={isInherited}
          >
            <option value="string">{fieldTypeIcons.string} String</option>
            <option value="number">{fieldTypeIcons.number} Number</option>
            <option value="boolean">{fieldTypeIcons.boolean} Boolean</option>
            <option value="date">{fieldTypeIcons.date} Date</option>
          </Select>
        </div>

        {/* Display Order */}
        <div>
          <Input
            label="Display Order"
            type="number"
            min="0"
            value={formData.displayOrder}
            onChange={(e) => handleChange('displayOrder', e.target.value)}
            helperText="Order in which fields appear (0 = first)"
          />
        </div>

        {/* Description */}
        <div className="col-span-2">
          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Optional help text or description for approvers"
            rows={3}
          />
        </div>

        {/* Steps */}
        <div>
          <Input
            label="Minimum Step"
            type="number"
            min="1"
            value={formData.minStep}
            onChange={(e) => handleChange('minStep', e.target.value)}
            placeholder="Optional"
            helperText="Minimum level that can edit (empty = all)"
          />
        </div>

        <div>
          <Input
            label="Maximum Step"
            type="number"
            min="1"
            value={formData.maxStep}
            onChange={(e) => handleChange('maxStep', e.target.value)}
            placeholder="Optional"
            helperText="Maximum level that can edit (empty = no limit)"
          />
        </div>

        {/* Validation Rules */}
        <div className="col-span-2">
          <Textarea
            label="Validation Rules (JSON)"
            value={formData.validationRules}
            onChange={(e) => handleChange('validationRules', e.target.value)}
            placeholder='{"min": 0, "max": 100, "pattern": "^[A-Z]+$"}'
            rows={3}
            className="font-mono text-sm"
            helperText="Optional JSON validation rules (e.g., min, max, pattern, maxLength)"
          />
        </div>

        {/* Toggles */}
        <div className="col-span-2 space-y-4 pt-4 border-t">
          <Checkbox
            label="Required Field - Must be filled before approval can proceed"
            checked={formData.isRequired}
            onChange={(e) => handleChange('isRequired', e.target.checked)}
          />

          <Checkbox
            label="Editable - Whether approvers can modify this field (if unchecked, field is display-only)"
            checked={formData.isEditable}
            onChange={(e) => handleChange('isEditable', e.target.checked)}
          />

          <Checkbox
            label="Show Value in List - When allowed values are present, display both name and value (e.g. 'Operations (CC-1001)') in the dropdown instead of just the name"
            checked={formData.showValueInList}
            onChange={(e) => handleChange('showValueInList', e.target.checked)}
            disabled={isInherited}
          />

          {isEditing && (
            <Checkbox
              label="Active - Whether this field is currently enabled"
              checked={formData.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
            />
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              {isEditing ? 'Update Field' : 'Create Field'}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

// Legacy alias for backward compatibility
export const EditableFieldForm = OrderAttributeForm;
